import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, bootstrapSession, setAccessToken, setUnauthorizedHandler } from './api';
import type { User } from './types';
import { invalidateFinancials } from './queries';

type AuthState = {
  user: User | null;
  /** True until the initial refresh attempt settles, so we don't flash the auth screen. */
  loading: boolean;
  /** Whether the server can send email — gates the verify banner and reset flow. */
  mailEnabled: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
};

const AuthContext = createContext<AuthState | null>(null);

type SessionResponse = { user: User; accessToken: string; expiresIn: number; mailEnabled: boolean };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [mailEnabled, setMailEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Cold start: the access token is gone (memory-only), but the httpOnly
  // refresh cookie may still be valid — trade it for a new session.
  //
  // Goes through `bootstrapSession` rather than a bare fetch so that
  // StrictMode's double-invoked effect shares one request. Refreshing rotates
  // the token, so two concurrent calls would revoke each other.
  useEffect(() => {
    let cancelled = false;
    void bootstrapSession()
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setUser(data.user as User);
          setMailEnabled(Boolean((data as { mailEnabled?: boolean }).mailEnabled));
        } else setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A 401 that survives a refresh means the session is genuinely gone.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      queryClient.clear();
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  // Refresh a few minutes before the 15-minute access token expires, so a user
  // sitting on a screen doesn't hit an avoidable round-trip on their next click.
  useEffect(() => {
    if (!user) return;
    const timer = setInterval(() => void bootstrapSession(), 12 * 60 * 1000);
    return () => clearInterval(timer);
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api<SessionResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    setMailEnabled(data.mailEnabled);
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const data = await api<SessionResponse>('/auth/register', {
      method: 'POST',
      body: { name, email, password },
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    setMailEnabled(data.mailEnabled);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const updateUser = useCallback(
    (next: User) => {
      setUser(next);
      if (next.currency !== user?.currency) invalidateFinancials(queryClient);
    },
    [queryClient, user?.currency],
  );

  const value = useMemo(
    () => ({ user, loading, mailEnabled, login, register, logout, updateUser }),
    [user, loading, mailEnabled, login, register, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
